import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type { ServerEvent } from '@leadgab/shared';
import { useAuth } from './auth.tsx';
import { useRealtimeEvent } from './realtime.tsx';

/**
 * A short tone when a customer writes in, if the agent asked for one.
 *
 * Synthesised rather than played from a file: two oscillator notes are a few
 * dozen lines and no binary in the repository, nothing to license, nothing to
 * fetch, and the pitch can be chosen rather than inherited from whatever sample
 * was to hand.
 *
 * The Settings toggle for this has existed since the rewrite and did nothing:
 * the preference was saved to the database and no code ever read it.
 */

const NOTES = [
  // Long enough to be heard as loud. The ear integrates level over roughly a
  // fifth of a second, so a blip shorter than that reads quieter than its
  // amplitude — which is why the first two attempts at "make it louder" bought
  // less than the numbers said they would. Still under half a second in total.
  { hz: 880, at: 0, seconds: 0.16 },
  { hz: 1174.7, at: 0.17, seconds: 0.26 },
];

/**
 * Gain at volume 100.
 *
 * Peak amplitude is not the same as loudness, and this is where the ceiling
 * ran out: a sine at 0.7 is already within 3 dB of clipping, so there was
 * almost nothing left to raise. What was left is the waveform. A square holds
 * its full amplitude for the whole cycle where a sine only touches it, which is
 * about 3 dB more actual level at the same peak, and its harmonics sit where
 * the ear is most sensitive — perceptually louder again, and for free.
 *
 * The lowpass is what makes that bearable rather than a buzzer: the harmonics
 * above it are the ones that read as cheap.
 *
 * 0.8 rather than something nearer 1.0 because the filter rings on every square
 * edge and the rendered peak comes out about 18% above the gain — measured, not
 * estimated; 0.85 clipped at 1.004. This leaves the output topping out around
 * 0.95.
 */
const MAX_PEAK = 0.8;
const TIMBRE_CUTOFF_HZ = 3400;
const TIMBRE_Q = 0.707;

/**
 * Amplitude is not loudness: halving the gain is nowhere near half as loud.
 * Squaring the slider position is the usual approximation, and it is what makes
 * the middle of the track sound like the middle rather than like almost-full.
 *
 * The old fixed level now sits at about 30 on the track, which is roughly where
 * "quiet" belongs on a scale whose top has to carry a noisy room.
 */
export function peakFor(volume: number): number {
  const clamped = Math.min(100, Math.max(0, volume));

  return MAX_PEAK * (clamped / 100) ** 2;
}

/**
 * Made on the first gesture, not on the first message.
 *
 * Browsers refuse to start audio until the page has been interacted with, and
 * a context created before that starts suspended. Building it on the first
 * click or keypress means it is ready and running by the time anything needs
 * to make a noise — an agent who has signed in has already clicked something.
 */
let context: AudioContext | null = null;

function ensureContext(): void {
  if (context) return;

  try {
    context = new AudioContext();
  } catch {
    // No Web Audio: the tab still blinks, which is the louder signal anyway.
  }
}

export function playMessageTone(volume: number): void {
  ensureContext();

  if (!context) return;

  const peak = peakFor(volume);
  if (peak <= 0) return;

  // A tab restored from the background can come back suspended.
  if (context.state === 'suspended') void context.resume();

  const now = context.currentTime;

  for (const note of NOTES) {
    const oscillator = context.createOscillator();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();

    oscillator.type = 'square';
    oscillator.frequency.value = note.hz;

    filter.type = 'lowpass';
    filter.frequency.value = TIMBRE_CUTOFF_HZ;
    filter.Q.value = TIMBRE_Q;

    // Ramped rather than switched: a square-edged start and stop is heard as a
    // click, which is the part people find irritating.
    const start = now + note.at;
    const end = start + note.seconds;

    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(peak, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);

    // Filtered before the envelope, so the fade in and out is applied to the
    // finished timbre — and so the gain stays the one number that decides level.
    oscillator.connect(filter).connect(gain).connect(context.destination);
    oscillator.start(start);
    oscillator.stop(end + 0.02);
  }
}

function fromCustomer(event: ServerEvent): boolean {
  if (event.type !== 'message') return false;

  const message = event.message as { user?: { id: number | null }; meta?: { system?: boolean } };

  return message.user?.id === null && !message.meta?.system;
}

export function useMessageSound(): void {
  const { me } = useAuth();
  const { pathname } = useLocation();

  const enabled = me?.preferences.soundEnabled ?? true;
  const volume = me?.preferences.soundVolume ?? 70;

  // Read inside the socket callback, which must not be rebuilt per render.
  const state = useRef({ enabled, pathname, volume });
  state.current = { enabled, pathname, volume };

  useEffect(() => {
    if (!enabled) return;

    const start = (): void => {
      ensureContext();
    };

    window.addEventListener('pointerdown', start, { once: true });
    window.addEventListener('keydown', start, { once: true });

    return () => {
      window.removeEventListener('pointerdown', start);
      window.removeEventListener('keydown', start);
    };
  }, [enabled]);

  useRealtimeEvent((event) => {
    if (!state.current.enabled || !fromCustomer(event)) return;

    const uuid = (event as { message: { sessionUuid?: string } }).message.sessionUuid ?? '';

    // Silent for the conversation already open and being watched: the message
    // is appearing on screen as it arrives, and a noise for something you are
    // reading is the kind of thing that gets the toggle turned off for good.
    const reading =
      state.current.pathname === `/chats/${uuid}` &&
      document.visibilityState === 'visible' &&
      document.hasFocus();

    if (reading) return;

    playMessageTone(state.current.volume);
  });
}
